import type {ComputedRef, Ref, ShallowRef} from 'vue';
import type {UseAbortableOptions} from './useAbortable';
import {useAbortable} from './useAbortable';

type PickByValueType<T, U> = {
    [K in keyof T as T[K] extends U ? K : never]: T[K];
};

/**
 * Transform any OpenAPI request into a abortable request
 *
 * @param apiController - The OpenAPI api controller
 * @param requestKey - The key of the request you want to use from the controller
 * @param options - Optional parameter to customize the behavior
 * @param options.automatedAbort - If true the previous request will be automatically aborted if a new one is started, defaults to true.
 *
 * @usage
 *
 * ```ts
 * import {UserApi} from '~/openapi';
 *
 * export const userStore = defineStore('user', () => {
 *
 *     const {execute, pending} = useAbortApi(UserApi, 'searchUser');
 *
 *     async function searchUser(query: string) {
 *         // This function will be aborted if a new search is triggered, and result will be undefined
 *         const result = await execute({query});
 *         if (result) {
 *             users.value = result
 *         }
 *     }
 * })
 * ```
 */
export function useAbortableApi<
    TApi extends Record<string, any>,
    TFilteredApi extends PickByValueType<TApi, (...args: any) => Promise<any>>,
    TKey extends keyof TFilteredApi,
    TFn extends (...args: any) => Promise<any> = TFilteredApi[TKey],
>(
    apiController: TApi,
    requestKey: TKey,
    options: Partial<UseAbortableOptions> = {},
): {
    execute: TFn;
    abort: () => void;
    aborted: Ref<boolean>;
    pending: ComputedRef<boolean>;
    error: Ref<any | null>;
    abortController: ShallowRef<AbortController | null>;
} {
    const {execute, abort, aborted, pending, error, abortController} = useAbortable(options);

    const executeAbortable: TFn = (async (args: Parameters<TFn>, init?: RequestInit) => {
        return await execute(async (signal) => {
            const initOverrides: RequestInit = {
                signal,
                ...(init && {...init}),
            };
            return await (apiController as any)[requestKey](args, initOverrides);
        });
    }) as any;

    return {execute: executeAbortable, abort, aborted, pending, error, abortController};
}
